/**
 * Generates cURL, TypeScript (fetch) and Python (requests) samples for every operation in the
 * reference, from the operation's own path, method and generated request example — never
 * hand-written per operation. Falls back to Zudoku's own snippet for any language we don't
 * handle (there are none today, but this keeps the door open).
 *
 * `GenerateCodeSnippetFn` (zudoku/dist/declarations/lib/plugins/openapi/interfaces.d.ts) is not
 * re-exported from the package's public entry points, so the parameter shape is declared here
 * structurally instead of imported — it only needs to satisfy `ApiOptionsSchema.generateCodeSnippet`
 * at the `zudoku.config.tsx` call site, which is a structural (not nominal) TypeScript check.
 */

type OperationParameter = {
  name: string;
  in: string;
  examples?: Array<{ value?: unknown }> | null;
  schema?: { example?: unknown } | null;
};

type SnippetOperation = {
  method: string;
  path: string;
  requestBody?: unknown;
  parameters?: OperationParameter[] | null;
};

export type GenerateCodeSnippetArgs = {
  selectedLang: string;
  selectedServer: string;
  operation: SnippetOperation;
  example?: unknown;
};

const TEST_KEY = 'sk_test_51NxSupaLabs...9f2';
const IDEMPOTENCY_SAMPLE = 'supalabs-2026-014';

function fillPathParams(path: string, parameters: OperationParameter[] | null | undefined): string {
  let filled = path;
  for (const param of parameters ?? []) {
    if (param.in !== 'path') continue;
    const example =
      (param.examples?.[0] as { value?: unknown } | undefined)?.value ??
      param.schema?.example ??
      param.name;
    filled = filled.replace(`{${param.name}}`, String(example));
  }
  return filled;
}

function toPythonLiteral(json: string): string {
  return json
    .replace(/: true/g, ': True')
    .replace(/: false/g, ': False')
    .replace(/: null/g, ': None');
}

export function generateCodeSnippet({
  selectedLang,
  selectedServer,
  operation,
  example,
}: GenerateCodeSnippetArgs): string | false {
  const method = (operation.method ?? 'get').toUpperCase();
  const path = fillPathParams(operation.path, operation.parameters);
  const url = `${selectedServer}${path}`;
  const hasBody = method !== 'GET' && method !== 'DELETE' && Boolean(operation.requestBody);
  const needsIdempotency = (operation.parameters ?? []).some((p) => p.name === 'Idempotency-Key');
  const bodyJson =
    hasBody && example !== undefined && example !== null
      ? JSON.stringify(example, null, 2)
      : undefined;

  if (selectedLang === 'shell') {
    const parts = [`curl ${url}`, `-X ${method}`, `-H "x-api-key: ${TEST_KEY}"`];
    if (hasBody) parts.push('-H "Content-Type: application/json"');
    if (needsIdempotency) parts.push(`-H "Idempotency-Key: ${IDEMPOTENCY_SAMPLE}"`);
    if (bodyJson) parts.push(`-d '${bodyJson}'`);
    return parts.join(' \\\n  ');
  }

  if (selectedLang === 'typescript') {
    const headerLines = [`"x-api-key": process.env.SWAPS_API_KEY!,`];
    if (hasBody) headerLines.push(`"Content-Type": "application/json",`);
    if (needsIdempotency) headerLines.push(`"Idempotency-Key": "${IDEMPOTENCY_SAMPLE}",`);
    const lines = [
      `const response = await fetch("${url}", {`,
      `  method: "${method}",`,
      `  headers: {`,
      ...headerLines.map((l) => `    ${l}`),
      `  },`,
    ];
    if (bodyJson) lines.push(`  body: JSON.stringify(${bodyJson}),`);
    lines.push(`});`, ``, `const data = await response.json();`);
    return lines.join('\n');
  }

  if (selectedLang === 'python') {
    const headerLines = [`"x-api-key": "${TEST_KEY}",`];
    if (needsIdempotency) headerLines.push(`"Idempotency-Key": "${IDEMPOTENCY_SAMPLE}",`);
    const lines = [
      `import requests`,
      ``,
      `headers = {`,
      ...headerLines.map((l) => `    ${l}`),
      `}`,
    ];
    if (bodyJson) {
      lines.push(``, `payload = ${toPythonLiteral(bodyJson)}`, ``);
      lines.push(
        `response = requests.${method.toLowerCase()}(`,
        `    "${url}",`,
        `    headers=headers,`,
        `    json=payload,`,
        `)`
      );
    } else {
      lines.push(``, `response = requests.${method.toLowerCase()}("${url}", headers=headers)`);
    }
    lines.push(``, `print(response.status_code, response.json())`);
    return lines.join('\n');
  }

  return false;
}
